'use client';

import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { CellTokenAmountPrice, MarketCell, NetworkAvatar as Network, type SrcAvatarProps } from '@morpho-org/ui-v6/client';
import { CellSimpleText, Skeleton } from '@morpho-org/ui-v6/server';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { abbreviateAddress } from '@repo/utils';
import { useMemo } from 'react';
import { ButtonLink, type ButtonLinkButtonProps } from '@/components/common/ButtonLink';
import { TimestampCell } from '@/components/common/Cells/TimestampCell';
import { InternalLink } from '@/components/common/InternalLink';
import { WarningIcon } from '@/components/common/WarningIcon';
import { getMarketUrl } from '@/helpers/routing';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import type { BorrowActivityTableRow } from './DashboardActivity.types';
import { BORROW_COLUMN_IDS, type DashboardActivityTableFeatures } from './DashboardActivity.config';
export const COLUMN_LABELS: Partial<Record<keyof typeof BORROW_COLUMN_IDS, string>> = {
  [BORROW_COLUMN_IDS.Timestamp]: 'Date',
  [BORROW_COLUMN_IDS.Type]: 'Type',
  [BORROW_COLUMN_IDS.AssetsUsd]: 'Amount',
  [BORROW_COLUMN_IDS.Vault]: 'Vault',
  [BORROW_COLUMN_IDS.TransactionHash]: 'Transaction',
  [BORROW_COLUMN_IDS.Network]: 'Network',
  [BORROW_COLUMN_IDS.Market]: 'Market'
};
const avatarProps: Omit<SrcAvatarProps, 'src'> = {
  size: 'small'
};
export const columnsDefs: ColumnDefWithSkeleton<DashboardActivityTableFeatures, BorrowActivityTableRow>[] = [{
  id: BORROW_COLUMN_IDS.Network,
  accessorFn: row => row.chainId,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.Network}</span>
        </TooltipTrigger>
        <TooltipContent>The network where the transaction occurred.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    return <div className="flex items-center">
          <Network chainId={row.original.chainId} srcAvatarProps={avatarProps} />
        </div>;
  },
  enableSorting: true,
  skeletonCell: <Skeleton className="h-md w-5" variant="circle" />,
  filterFn: (row, _, filterValue) => filterValue.includes(row.original.chainId)
}, {
  id: BORROW_COLUMN_IDS.Timestamp,
  accessorFn: row => row.timestamp,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.Timestamp}</span>
        </TooltipTrigger>
        <TooltipContent>Timestamp of when the transaction occurred.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    return <TimestampCell timestamp={row.original.timestamp} />;
  },
  skeletonCell: <Skeleton className="h-md w-[122px] rounded-[20px]" />,
  enableSorting: true
}, {
  id: BORROW_COLUMN_IDS.Market,
  header: () => <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.Market}</span>,
  cell: function MarketLinkCell({
    row
  }) {
    const {
      isSelf
    } = useViewedAccountAddress();
    const identifier = InterfaceChainUtils.getIdentifierById(row.original.chainId);
    const collateralSymbol = row.original.collateralAsset?.symbol;
    const loanSymbol = row.original.loanAsset?.symbol;
    const marketUrl = row.original.marketId && collateralSymbol && loanSymbol ? getMarketUrl({
      identifier,
      marketId: row.original.marketId,
      collateralSymbol,
      loanSymbol,
      hash: isSelf ? 'yourPosition' : undefined
    }) : undefined;
    if (!marketUrl) {
      return <MarketCell collateralAsset={row.original.collateralAsset} loanAsset={row.original.loanAsset} chainId={row.original.chainId} renderWarnings={<WarningIcon warnings={row.original.warnings} />} lltv={row.original.lltv} />;
    }
    return <InternalLink href={marketUrl} withHoverEffect>
          <MarketCell collateralAsset={row.original.collateralAsset} loanAsset={row.original.loanAsset} chainId={row.original.chainId} renderWarnings={<WarningIcon warnings={row.original.warnings} />} lltv={row.original.lltv} className="gap-2xs" />
        </InternalLink>;
  },
  enableSorting: false,
  skeletonCell: <Skeleton className="h-md w-[159px] rounded-[20px]" />
}, {
  id: BORROW_COLUMN_IDS.Type,
  accessorFn: row => row.type,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.Type}</span>
        </TooltipTrigger>
        <TooltipContent>
          The nature of the transaction, providing more details about the type of transaction.
        </TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => <CellSimpleText>{row.original.typeText}</CellSimpleText>,
  enableSorting: false,
  skeletonCell: <Skeleton className="h-md w-[110px] rounded-[20px]" />,
  filterFn: 'arrIncludesSome'
}, {
  id: BORROW_COLUMN_IDS.AssetsUsd,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.AssetsUsd}</span>
        </TooltipTrigger>
        <TooltipContent>The amount of tokens involved in the transaction.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    return <CellTokenAmountPrice token={row.original.token} amount={row.original.amount} value={row.original.usdValue} withAvatar />;
  },
  enableSorting: false,
  skeletonCell: <Skeleton className="h-md w-[147px] rounded-[20px]" />
}, {
  id: BORROW_COLUMN_IDS.TransactionHash,
  accessorFn: row => row.hash,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{
        marginLeft: '12px'
      }}>
            {COLUMN_LABELS.TransactionHash}
          </span>
        </TooltipTrigger>
        <TooltipContent>The transaction hash.</TooltipContent>
      </Tooltip>,
  cell: function TransactionHashCell({
    row
  }) {
    const buttonProps = useMemo<ButtonLinkButtonProps>(() => ({
      variant: 'ghost',
      size: 'default',
      className: 'hover:bg-accent active:bg-accent hover:brightness-100',
      label: abbreviateAddress(row.original.hash)
    }), [row.original.hash]);
    return <ButtonLink href={row.original.urlHash} mode="external" buttonProps={buttonProps} />;
  },
  enableSorting: false,
  skeletonCell: <Skeleton className="h-md w-[122px] rounded-[20px]" />,
  filterFn: 'includesString'
}];